import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CrossfadePanel } from '../../../components/motion/index';
import {
  defaultSharedCapTableSettings,
  SharedCapTableExpireInterval,
  SharedCapTableLink,
  SharedCapTableSettings,
} from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { StringKey } from '../../../lang/index';
import { resolveSharePasswordState } from './share-password-state.util';
import { ContentTab } from './tabs/content-tab';
import { PrivacyTab } from './tabs/privacy-tab';
import { ViewOptionsTab } from './tabs/view-options-tab';

export enum ShareModalTab {
  CONTENT = 'content',
  PRIVACY = 'privacy',
  VIEW_OPTIONS = 'view-options',
}

const TAB_TITLES: [ShareModalTab, StringKey][] = [
  [ShareModalTab.CONTENT, 'SHARE_CONTENT'],
  [ShareModalTab.VIEW_OPTIONS, 'SHARE_VIEW_OPTIONS'],
  [ShareModalTab.PRIVACY, 'SHARE_PRIVACY'],
];

export type ShareLinkFormValues = {
  /**
   * `undefined` — the owner did not touch the expiry control, so the stored `expiresAt` must be
   * left alone; `null` — protection removed; otherwise the new interval. An existing link stores a
   * date, not the interval it was created from, so the interval cannot be reconstructed for the
   * form — which is why "untouched" has to be its own state rather than defaulting to `null`.
   */
  expireInterval: null | SharedCapTableExpireInterval | undefined;
  note: string;
  password: null | string;
  settings: SharedCapTableSettings;
};

type ShareCapTableModalProps = {
  editedLink?: SharedCapTableLink;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (values: ShareLinkFormValues) => void;
};

export const ShareCapTableModal: FC<ShareCapTableModalProps> = ({
  editedLink,
  isPending,
  onCancel,
  onSubmit,
}) => {
  const isEditMode = !!editedLink;
  const [selectedTab, setSelectedTab] = useState(ShareModalTab.CONTENT);
  const [values, setValues] = useState<ShareLinkFormValues>({
    // Untouched on an existing link; a new link starts with no expiry.
    expireInterval: isEditMode ? undefined : null,
    note: editedLink?.note ?? '',
    // `null` means protection is off. An already-protected link starts empty with the toggle on:
    // a bcrypt hash cannot be shown back, and submitting it untouched keeps the stored password.
    password: editedLink?.hasPassword ? '' : null,
    settings: editedLink?.settings ?? defaultSharedCapTableSettings(),
  });

  const { isIncomplete: isPasswordIncomplete } = resolveSharePasswordState({
    hasStoredPassword: !!editedLink?.hasPassword,
    password: values.password,
  });

  const hasSelectedColumns = Object.values(values.settings.columns).some(
    (columns) => columns.length > 0,
  );

  return (
    // The height is fixed rather than fitted, on both breakpoints, so the footer holds still as
    // the viewer moves between tabs of very different lengths — the cross-fade below would resize
    // mid-transition otherwise. Content outgrows it and its body scrolls.
    <div className="flex h-[90vh] max-h-[90vh] w-full max-w-[493px] flex-col rounded-lg bg-white p-4 xs:h-[754px]">
      <div className="flex h-10 shrink-0 items-center">
        <span className="text-xl font-semibold leading-8 text-gray-700">
          <AppFormattedMessage id="SHARE" />
        </span>
      </div>

      <div className="mt-2 flex shrink-0 gap-4 border-b border-b-gray-100">
        {TAB_TITLES.map(([tabKey, tabTitle]) => (
          <button
            className={twMerge(
              'relative px-1 pb-4 text-sm font-[450] text-gray-400 transition-colors',
              selectedTab === tabKey && 'font-[550] text-brand-700',
            )}
            key={tabKey}
            onClick={() => setSelectedTab(tabKey)}
            type="button"
          >
            <span className="text-nowrap">
              <AppFormattedMessage id={tabTitle} />
            </span>
            {selectedTab === tabKey && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-brand-700" />
            )}
          </button>
        ))}
      </div>

      {/* `mode="wait"` so the outgoing tab finishes before the next arrives: overlapping them puts
          two panels in the scroll container at once and the scrollbar flickers. */}
      <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <AnimatePresence mode="wait">
          <CrossfadePanel className="flex flex-col" key={selectedTab} panelKey={selectedTab}>
            {selectedTab === ShareModalTab.CONTENT && (
              <ContentTab
                note={values.note}
                onNoteChange={(note) => setValues((prev) => ({ ...prev, note }))}
                onSettingsChange={(settings) => setValues((prev) => ({ ...prev, settings }))}
                settings={values.settings}
              />
            )}

            {selectedTab === ShareModalTab.VIEW_OPTIONS && (
              <ViewOptionsTab
                onSettingsChange={(settings) => setValues((prev) => ({ ...prev, settings }))}
                settings={values.settings}
              />
            )}

            {selectedTab === ShareModalTab.PRIVACY && (
              <PrivacyTab
                expireInterval={values.expireInterval}
                hasStoredPassword={!!editedLink?.hasPassword}
                isEditMode={isEditMode}
                onAllowExportChange={(settings) => setValues((prev) => ({ ...prev, settings }))}
                onExpireIntervalChange={(expireInterval) =>
                  setValues((prev) => ({ ...prev, expireInterval }))
                }
                onPasswordChange={(password) => setValues((prev) => ({ ...prev, password }))}
                password={values.password}
                settings={values.settings}
                storedExpiresAt={editedLink?.expiresAt ?? null}
              />
            )}
          </CrossfadePanel>
        </AnimatePresence>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4 pt-4">
        <Button
          className="h-11 w-[94px] rounded border border-gray-300 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className="h-11 w-[180px] rounded text-sm font-[550] text-white"
          disabled={isPending || isPasswordIncomplete || !hasSelectedColumns}
          isLoading={isPending}
          onClick={() => onSubmit(values)}
        >
          <AppFormattedMessage id={isEditMode ? 'SAVE' : 'CREATE_SHARING_LINK'} />
        </Button>
      </div>
    </div>
  );
};
